import { useMemo } from "react";
import { ArrowUpRight, MapPinned } from "lucide-react";
import CarouselRail from "@/components/yts/CarouselRail";
import ExperienceCard from "@/components/yts/ExperienceCard";
import { Section } from "@/components/yts/HomeSections";
import { useExplore, toCard, photosFirst } from "@/lib/ExploreContext";
import { catMatches, subcategoriesFor, subMatches, CHINCHORROS_KIOSKS, VEGAN_SUB } from "@/data/taxonomy";
import { useLang } from "@/lib/i18n";
import { categoryLabel } from "@/lib/locales";

const CATEGORY_TITLES = {
  Gastronomy: "Gastronomy", "Eat & Drink": "Gastronomy",
  Eat: "Gastronomy", Drink: "Gastronomy", Nightlife: "Nightlife",
  Shop: "Local Shops", Stays: "Stays",
};
const MAX_RAILS = 8;
const PER_COLLECTION = 14;

// Deliberate ordering is easier to browse than random reshuffling between
// renders. Give priority to businesses with their own genuine cover images,
// then a stable alphabetical tiebreaker; never fabricate popularity.
const arrange = (items) => photosFirst([...(items || [])].sort((a, b) =>
  String(a.name || "").localeCompare(String(b.name || ""), "es")
));
const leadRank = (sub) => sub === CHINCHORROS_KIOSKS ? 0 : sub === VEGAN_SUB ? 1 : 2;
const cardFor = (item, nbNames) => toCard(item, nbNames);
const itemLabel = (count, es) => es
  ? `${count} ${count === 1 ? "lugar" : "lugares"}`
  : `${count} ${count === 1 ? "place" : "places"}`;

export default function CategoryView({ category, listings = [], loading, filterSubs = new Set(), clearFilters }) {
  const { nbNames, setFilterSubs } = useExplore();
  const { t, lang } = useLang();
  const es = lang === "es";
  const title = categoryLabel(CATEGORY_TITLES[category] || category, lang);
  const selected = [...filterSubs];
  const options = subcategoriesFor(category);
  const matching = useMemo(() => arrange(listings.filter((item) =>
    item?.id && item?.name &&
    (category !== "Stays" || catMatches(item, "Stays")) &&
    item.status !== "Archived" && item.status !== "Draft" && !item.coming_soon
  )), [listings, category]);

  const groups = useMemo(() => {
    if (category === "Stays") {
      return [{ sub:"Stays", items: matching.map((item) => cardFor(item, nbNames)).filter(Boolean) }];
    }
    const seen = new Set();
    const buckets = (selected.length ? selected : options).map((sub) => {
      const raw = matching.filter((item) => !seen.has(item.id) && subMatches(item, sub));
      raw.forEach((item) => seen.add(item.id));
      return { sub, items: raw.map((item) => cardFor(item, nbNames)).filter(Boolean) };
    }).filter((group) => group.items.length > 0)
      .sort((a, b) => leadRank(a.sub) - leadRank(b.sub) || b.items.length - a.items.length);

    // Single-place rails make a directory feel empty. Keep those real
    // businesses visible, but collect them in a meaningful discovery rail.
    const substantial = selected.length ? buckets : buckets.filter((group) => group.items.length >= 2);
    const sparse = selected.length ? [] : buckets.filter((group) => group.items.length < 2);
    const chosen = substantial.slice(0, MAX_RAILS);
    const shownIds = new Set(chosen.flatMap((group) => group.items.map((item) => item.id)));
    const remainder = [...sparse, ...substantial.slice(MAX_RAILS)]
      .flatMap((group) => group.items);
    const ungrouped = matching.filter((item) => !seen.has(item.id))
      .map((item) => cardFor(item, nbNames)).filter(Boolean);
    const more = [...remainder, ...ungrouped].filter((item) => !shownIds.has(item.id));
    if (more.length) chosen.push({ sub:"more", items:more });
    return chosen;
  }, [category, matching, nbNames, options.join("|"), selected.join("|")]);

  const allCount = matching.length;
  const reset = () => { setFilterSubs(new Set()); clearFilters?.(); };
  const focusSub = (sub) => {
    setFilterSubs(new Set([sub]));
    const target = document.getElementById("category-results");
    if (target) target.scrollIntoView({ behavior:"smooth", block:"start" });
  };

  return (
    <div className="min-h-[260px] pb-10">
      <Section id="category-results" className="scroll-mt-[164px]">
        <div className="border-t-2 border-[#171719] pb-2 pt-4 dark:border-white">
          <p className="text-[10px] font-bold uppercase tracking-[.12em] text-[#a53b37] dark:text-[#fbaba7]">
            {es ? "YA TÚ SABES PR / EXPLORA" : "YA TÚ SABES PR / EXPLORE"}
          </p>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-[clamp(28px,6.2vw,52px)] font-extrabold leading-[1.05] tracking-[-.03em]">
                {title}
              </h1>
              <p className="mt-2 max-w-[55ch] text-[13px] leading-relaxed text-[#706d68] dark:text-white/65">
                {es
                  ? "Colecciones locales para explorar a tu manera. Desliza para ver más, o elige un tipo y barrio arriba."
                  : "Local collections to explore your way. Swipe to see more, or select a type and neighborhood above."}
              </p>
            </div>
            {!loading && (
              <span className="text-[11px] font-bold uppercase tracking-[.08em] text-[#817d76] dark:text-white/70">
                {itemLabel(allCount, es)}
              </span>
            )}
          </div>
        </div>
      </Section>

      {loading ? (
        <Section><p role="status" className="py-6 text-sm text-[#706d68]">{es ? "Cargando lugares…" : "Loading places…"}</p></Section>
      ) : groups.length ? groups.map((group, index) => {
        const focused = selected.includes(group.sub) || category === "Stays" || group.sub === "more";
        const cards = focused ? group.items : group.items.slice(0, PER_COLLECTION);
        const railTitle = group.sub === "more"
          ? (es ? `Más en ${title}` : `More in ${title}`)
          : categoryLabel(group.sub, lang);
        return (
          <CarouselRail
            key={group.sub}
            editorial={index === 0}
            title={railTitle}
            subtitle={itemLabel(group.items.length, es)}
            itemWidth="w-[min(70vw,270px)] min-w-[205px] sm:w-[260px] sm:min-w-0"
            action={!focused && group.items.length > PER_COLLECTION && group.sub !== "more" ? (
              <button type="button" onClick={() => focusSub(group.sub)}
                className="inline-flex min-h-10 items-center gap-1 text-[11px] font-bold underline underline-offset-4">
                {es ? "Ver todos" : "See all"} <ArrowUpRight size={15}/>
              </button>
            ) : null}
          >
            {cards.map((item) => <ExperienceCard key={item.id} item={item} />)}
          </CarouselRail>
        );
      }) : (
        <Section>
          <div className="py-9 text-center">
            <p className="text-[15px] font-semibold">{t("category.noMatch")}</p>
            <button type="button" onClick={reset}
              className="mt-3 inline-flex min-h-10 items-center rounded-md border border-black/20 px-4 text-[12px] font-bold dark:border-white/30">
              {t("results.clear")}
            </button>
          </div>
        </Section>
      )}
      {!loading && groups.length > 0 && (
        <Section>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/10 pb-3 pt-4 text-[12px] text-[#706d68] dark:border-white/15 dark:text-white/65">
            <span>{es ? "Cada lugar tiene su propia página, ubicación y detalles." : "Every place connects to its own page, location and details."}</span>
            <a href="/map" className="inline-flex min-h-10 items-center gap-2 font-bold text-[#171719] underline underline-offset-4 dark:text-white">
              <MapPinned size={16} aria-hidden="true"/> {es ? "Explorar en el mapa" : "Explore on the map"}
            </a>
          </div>
        </Section>
      )}
    </div>
  );
}
